import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Upload,
  FileText,
  Link2,
  ClipboardPaste,
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Users,
  Download,
  Info,
} from "lucide-react";

import useContactImport from "../hooks/useContactImport";
import { OUR_FIELDS } from "../hooks/useImportContact";

const STEPS = [
  { key: "source", label: "Source" },
  { key: "map", label: "Map columns" },
  { key: "result", label: "Done" },
];

const NOT_MAPPED = "";

const SAMPLE_CSV = [
  "Name,Phone,Email,City,Company",
  "Ada Lovelace,+15550102030,ada@example.com,London,Analytical Engines",
  "Alan Turing,+15550607080,alan@example.com,Manchester,Bletchley",
  "Grace Hopper,+15551234567,grace@example.com,New York,US Navy",
].join("\n");

function downloadSampleCsv() {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "auto-appy-contacts-sample.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** How-to guidance shown on the first step. */
function InstructionsPanel() {
  return (
    <div className="mb-4 rounded-xl border border-border-light bg-slate-50/70 p-3.5 dark:border-dark-border-light dark:bg-dark-surface/70">
      <div className="mb-2 flex items-center gap-1.5">
        <Info size={13} className="text-primary dark:text-dark-primary" />
        <p className="text-[11px] font-bold uppercase tracking-wide text-primary dark:text-dark-primary">
          How to format your data
        </p>
      </div>

      <ul className="space-y-1.5 text-[11px] leading-5 text-text-secondary dark:text-dark-text-secondary">
        <li>
          <span className="font-semibold text-text-primary dark:text-dark-text-primary">
            First row = column headers.
          </span>{" "}
          One contact per row after that.
        </li>
        <li>
          <span className="font-semibold text-danger">Phone number — required.</span>{" "}
          Include the country code, e.g. <code className="rounded bg-white px-1 dark:bg-black/20">+15551234567</code>.
          Spaces, dashes and brackets are fine. Rows without a valid phone number
          are skipped.
        </li>
        <li>
          <span className="font-semibold text-text-primary dark:text-dark-text-primary">
            Recommended:
          </span>{" "}
          <b>Name</b>, and optionally <b>Email</b>, <b>Country code</b>,
          <b> WhatsApp ID</b>, <b>Status</b>, <b>Notes</b>.
        </li>
        <li>
          <span className="font-semibold text-text-primary dark:text-dark-text-primary">
            Any other columns
          </span>{" "}
          (City, Company, Plan, Signup date…) are kept on each contact as
          <b> metadata</b> — you pick which ones on the next step.
        </li>
        <li>
          Column names don’t have to match exactly — “Mobile”, “Full Name” etc.
          are detected automatically and you can adjust the mapping.
        </li>
        <li>
          Re-importing is safe: a contact with the same phone number is
          <b> updated</b>, not duplicated. Up to 5,000 rows per import.
        </li>
      </ul>

      <button
        type="button"
        onClick={downloadSampleCsv}
        className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-border-color bg-card-bg px-2.5 py-1.5 text-[11px] font-semibold text-primary transition hover:bg-theme-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-primary"
      >
        <Download size={12} />
        Download sample CSV
      </button>
    </div>
  );
}

function ImportContactsModal({ open = false, onClose, onImported }) {
  const imp = useContactImport();
  const {
    step,
    reset,
    loading,
    importing,
    error,
    columns,
    rows,
    totalRows,
    truncated,
    sourceLabel,
    mapping,
    metadataColumns,
    result,
    loadFromFile,
    loadFromPaste,
    loadFromGoogleSheet,
    setFieldMapping,
    toggleMetadataColumn,
    runImport,
    setStep,
  } = imp;

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  if (!open) return null;

  const busy = loading || importing;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-[#0f2b42]/30 backdrop-blur-[5px]"
        onClick={busy ? undefined : onClose}
      />

      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl border border-border-color bg-card-bg shadow-elevated dark:border-dark-border dark:bg-dark-card">
        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-light px-5 py-4 dark:border-dark-border-light">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
              <Users size={18} />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-text-primary dark:text-dark-text-primary">
                Import contacts
              </h2>
              <p className="truncate text-[11px] text-text-muted dark:text-dark-text-muted">
                From a CSV, a public Google Sheet, or pasted rows
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition hover:bg-theme-50 hover:text-primary disabled:opacity-40 dark:text-dark-text-muted dark:hover:bg-dark-soft-blue"
          >
            <X size={16} />
          </button>
        </div>

        {/* STEP RAIL */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border-light px-5 py-2.5 dark:border-dark-border-light">
          {STEPS.map((s, i) => {
            const active = s.key === step;
            const done = STEPS.findIndex((x) => x.key === step) > i;
            return (
              <React.Fragment key={s.key}>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                      active
                        ? "bg-primary text-white"
                        : done
                          ? "bg-primary/20 text-primary dark:bg-dark-primary/20 dark:text-dark-primary"
                          : "bg-theme-100 text-text-muted dark:bg-dark-soft-blue dark:text-dark-text-muted"
                    }`}
                  >
                    {done ? <CheckCircle2 size={12} /> : i + 1}
                  </span>
                  <span
                    className={`text-[11px] font-medium ${
                      active
                        ? "text-text-primary dark:text-dark-text-primary"
                        : "text-text-muted dark:text-dark-text-muted"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="h-px flex-1 bg-border-light dark:bg-dark-border-light" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* BODY */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {error ? (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 dark:border-red-900/40 dark:bg-red-950/20">
              <AlertCircle size={15} className="mt-0.5 shrink-0 text-danger" />
              <p className="text-xs leading-5 text-red-700 dark:text-red-400">{error}</p>
            </div>
          ) : null}

          {step === "source" && (
            <SourceStep
              loading={loading}
              onFile={loadFromFile}
              onPaste={loadFromPaste}
              onGoogleSheet={loadFromGoogleSheet}
            />
          )}

          {step === "map" && (
            <MapStep
              columns={columns}
              rows={rows}
              totalRows={totalRows}
              truncated={truncated}
              sourceLabel={sourceLabel}
              mapping={mapping}
              metadataColumns={metadataColumns}
              onFieldMapping={setFieldMapping}
              onToggleMetadata={toggleMetadataColumn}
            />
          )}

          {step === "result" && <ResultStep result={result} />}
        </div>

        {/* FOOTER */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border-light bg-card-bg/95 px-5 py-3 dark:border-dark-border-light dark:bg-dark-card/95">
          {step === "map" ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setStep("source")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-color bg-card-bg px-3 py-2 text-xs font-semibold text-text-secondary transition hover:bg-theme-50 disabled:opacity-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
            >
              <ArrowLeft size={14} />
              Back
            </button>
          ) : (
            <span />
          )}

          {step === "map" && (
            <button
              type="button"
              disabled={busy || !mapping.phone_number}
              onClick={runImport}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {importing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowRight size={14} />
              )}
              {importing
                ? "Importing…"
                : `Import ${rows.length} contact${rows.length === 1 ? "" : "s"}`}
            </button>
          )}

          {step === "result" && (
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={reset}
                className="rounded-lg border border-border-color bg-card-bg px-3 py-2 text-xs font-semibold text-text-secondary transition hover:bg-theme-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
              >
                Import more
              </button>
              <button
                type="button"
                onClick={() => {
                  onImported?.();
                  onClose?.();
                }}
                className="rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white transition hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          )}

          {step === "source" && (
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="ml-auto rounded-lg border border-border-color bg-card-bg px-3 py-2 text-xs font-semibold text-text-secondary transition hover:bg-theme-50 disabled:opacity-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 1 — source                                                     */
/* ------------------------------------------------------------------ */

function SourceStep({ loading, onFile, onPaste, onGoogleSheet }) {
  const [tab, setTab] = useState("file");
  const [sheetUrl, setSheetUrl] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const TABS = [
    { key: "file", label: "Upload file", icon: FileText },
    { key: "google_sheet", label: "Google Sheet", icon: Link2 },
    { key: "paste", label: "Paste data", icon: ClipboardPaste },
  ];

  return (
    <div>
      <InstructionsPanel />

      <div className="mb-4 grid grid-cols-3 gap-2">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-[11px] font-semibold transition ${
              tab === key
                ? "border-primary bg-primary/5 text-primary dark:border-dark-primary dark:bg-dark-soft-blue dark:text-dark-primary"
                : "border-border-color text-text-secondary hover:border-theme-300 hover:bg-theme-50 dark:border-dark-border dark:text-dark-text-secondary"
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {tab === "file" && (
        <>
          <button
            type="button"
            disabled={loading}
            onClick={() => fileRef.current?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onFile(f);
            }}
            className={`flex min-h-[180px] w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 transition ${
              dragging
                ? "border-primary bg-primary/5"
                : "border-border-color bg-slate-50/70 hover:border-theme-300 hover:bg-theme-50 dark:border-dark-border dark:bg-dark-surface/60"
            } ${loading ? "cursor-wait opacity-60" : "cursor-pointer"}`}
          >
            {loading ? (
              <Loader2 size={24} className="animate-spin text-primary" />
            ) : (
              <Upload size={24} className="text-primary" />
            )}
            <p className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">
              {loading ? "Reading file…" : "Drop a CSV here, or click to browse"}
            </p>
            <p className="text-[11px] text-text-muted dark:text-dark-text-muted">
              Working in Excel? Use File → Save As → CSV, then upload that.
            </p>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onFile(f);
            }}
          />
        </>
      )}

      {tab === "google_sheet" && (
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-secondary dark:text-dark-text-secondary">
              Public Google Sheet link
            </label>
            <input
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              className="w-full rounded-xl border border-border-color bg-card-bg px-3.5 py-2.5 text-sm text-text-primary outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-primary"
            />
          </div>
          <p className="rounded-xl border border-border-light bg-slate-50/70 px-3 py-2.5 text-[11px] leading-5 text-text-muted dark:border-dark-border-light dark:bg-dark-surface/70 dark:text-dark-text-muted">
            In Google Sheets: <b>Share → General access → “Anyone with the link” →
            Viewer</b>. We read the first tab of the sheet.
          </p>
          <button
            type="button"
            disabled={loading || !sheetUrl.trim()}
            onClick={() => onGoogleSheet(sheetUrl)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white transition hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            {loading ? "Loading…" : "Load sheet"}
          </button>
        </div>
      )}

      {tab === "paste" && (
        <div className="space-y-3">
          <textarea
            rows={8}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Name,Phone,City\nAda,+15550102030,London\nAlan,+15550607080,Manchester"}
            className="w-full resize-none rounded-xl border border-border-color bg-card-bg px-3.5 py-2.5 font-mono text-xs leading-5 text-text-primary outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-primary"
          />
          <p className="text-[11px] text-text-muted dark:text-dark-text-muted">
            Paste straight from a spreadsheet (tab-separated) or type CSV. Keep the
            first row as headers.
          </p>
          <button
            type="button"
            disabled={loading || !pasteText.trim()}
            onClick={() => onPaste(pasteText)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white transition hover:bg-primary/90 disabled:opacity-50"
          >
            <ClipboardPaste size={14} />
            Load data
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 2 — map columns                                                */
/* ------------------------------------------------------------------ */

function MapStep({
  columns,
  rows,
  totalRows,
  truncated,
  sourceLabel,
  mapping,
  metadataColumns,
  onFieldMapping,
  onToggleMetadata,
}) {
  const mappedColumns = useMemo(
    () => new Set(Object.values(mapping).filter(Boolean)),
    [mapping],
  );

  const preview = rows.slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-secondary dark:text-dark-text-secondary">
        <CheckCircle2 size={14} className="text-success" />
        <span className="font-semibold text-text-primary dark:text-dark-text-primary">
          {totalRows} row{totalRows === 1 ? "" : "s"}
        </span>
        <span>from {sourceLabel}</span>
        {truncated && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
            first {rows.length} will be imported
          </span>
        )}
      </div>

      {/* field mapping */}
      <div className="overflow-hidden rounded-xl border border-border-color dark:border-dark-border">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wide text-text-muted dark:bg-dark-surface/70 dark:text-dark-text-muted">
            <tr>
              <th className="px-3 py-2 font-semibold">Our field</th>
              <th className="px-3 py-2 font-semibold">Your column</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-light dark:divide-dark-border-light">
            {OUR_FIELDS.map((field) => {
              const invalid = field.required && !mapping[field.key];
              return (
                <tr key={field.key}>
                  <td className="px-3 py-2 font-medium text-text-primary dark:text-dark-text-primary">
                    {field.label}
                    {field.required && <span className="ml-0.5 text-danger">*</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="relative">
                      <select
                        value={mapping[field.key] || NOT_MAPPED}
                        onChange={(e) => onFieldMapping(field.key, e.target.value)}
                        className={`w-full appearance-none rounded-lg border bg-card-bg px-2.5 py-1.5 pr-7 text-xs outline-none transition focus:border-primary dark:bg-dark-surface ${
                          invalid
                            ? "border-red-300 text-danger"
                            : "border-border-color text-text-primary dark:border-dark-border dark:text-dark-text-primary"
                        }`}
                      >
                        <option value={NOT_MAPPED}>— not mapped —</option>
                        {columns.map((col) => (
                          <option
                            key={col}
                            value={col}
                            disabled={
                              mappedColumns.has(col) && mapping[field.key] !== col
                            }
                          >
                            {col}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={13}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-text-muted"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* metadata columns */}
      <div>
        <p className="text-xs font-semibold text-text-primary dark:text-dark-text-primary">
          Extra columns → saved to each contact’s metadata
        </p>
        <p className="mb-2 text-[11px] text-text-muted dark:text-dark-text-muted">
          Toggle a column off to ignore it. Turned-on columns are stored as
          <code className="mx-1 rounded bg-slate-100 px-1 dark:bg-black/20">{`{ "City": "London" }`}</code>
          on the contact.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {columns.filter((c) => !mappedColumns.has(c)).length === 0 && (
            <span className="text-[11px] text-text-muted dark:text-dark-text-muted">
              Every column is mapped to a field.
            </span>
          )}
          {columns
            .filter((c) => !mappedColumns.has(c))
            .map((col) => {
              const on = metadataColumns.includes(col);
              return (
                <button
                  key={col}
                  type="button"
                  onClick={() => onToggleMetadata(col)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                    on
                      ? "border-primary bg-primary/10 text-primary dark:border-dark-primary dark:bg-dark-soft-blue dark:text-dark-primary"
                      : "border-border-color text-text-muted line-through dark:border-dark-border dark:text-dark-text-muted"
                  }`}
                >
                  {col}
                </button>
              );
            })}
        </div>
      </div>

      {/* preview */}
      {preview.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted dark:text-dark-text-muted">
            Preview
          </p>
          <div className="overflow-x-auto rounded-xl border border-border-color dark:border-dark-border">
            <table className="w-full text-left text-[11px]">
              <thead className="bg-slate-50/80 text-text-muted dark:bg-dark-surface/70">
                <tr>
                  <th className="whitespace-nowrap px-3 py-1.5">Name</th>
                  <th className="whitespace-nowrap px-3 py-1.5">Phone</th>
                  <th className="whitespace-nowrap px-3 py-1.5">Metadata</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light dark:divide-dark-border-light">
                {preview.map((row, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-text-primary dark:text-dark-text-primary">
                      {mapping.name ? row[mapping.name] || "—" : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-text-primary dark:text-dark-text-primary">
                      {mapping.phone_number ? row[mapping.phone_number] || "—" : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-text-muted dark:text-dark-text-muted">
                      {metadataColumns
                        .map((c) => (row[c] ? `${c}: ${row[c]}` : null))
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 3 — result                                                     */
/* ------------------------------------------------------------------ */

function ResultStep({ result }) {
  const [showErrors, setShowErrors] = useState(false);

  if (!result) {
    return (
      <p className="py-8 text-center text-sm text-text-muted dark:text-dark-text-muted">
        No import summary was returned.
      </p>
    );
  }

  const stats = [
    { label: "Added", value: result.created, tone: "text-success" },
    { label: "Updated", value: result.updated, tone: "text-primary" },
    { label: "Skipped", value: result.skipped, tone: "text-text-muted" },
    { label: "Failed", value: result.failed, tone: "text-danger" },
  ];

  return (
    <div className="py-2">
      <div className="mb-5 flex flex-col items-center gap-2 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success">
          <CheckCircle2 size={26} />
        </div>
        <p className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">
          Import finished
        </p>
        <p className="text-xs text-text-muted dark:text-dark-text-muted">
          {result.total} row{result.total === 1 ? "" : "s"} processed
        </p>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-border-color px-2 py-3 text-center dark:border-dark-border"
          >
            <p className={`text-lg font-bold ${s.tone}`}>{s.value ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wide text-text-muted dark:text-dark-text-muted">
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {Array.isArray(result.errors) && result.errors.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowErrors((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-danger"
          >
            <ChevronDown
              size={14}
              className={`transition-transform ${showErrors ? "rotate-180" : ""}`}
            />
            {result.errors.length} row{result.errors.length === 1 ? "" : "s"} had problems
          </button>
          {showErrors && (
            <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-xl border border-border-light bg-slate-50/70 p-3 text-[11px] text-text-secondary dark:border-dark-border-light dark:bg-dark-surface/70 dark:text-dark-text-secondary">
              {result.errors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default ImportContactsModal;
